"use client";

import { useMemo, type CSSProperties } from "react";

import { useBuiltInFontsLoader } from "@/features/brandKits/hooks/useBuiltInFontsLoader";
import { useBrandKitFontFaces } from "@/features/brandKits/hooks/useBrandKitFontFaces";
import { useLomdaV3Editor } from "../store/useLomdaV3Editor";
import {
    DEFAULT_THEME,
    DEFAULT_INNER_GAP,
    buildThemeVars,
    buildFontWeightVars,
    CONTENT_ROOT_FONT,
} from "../lib/theme";

/**
 * Resolves the lomda's theme into CSS variables. Apply on the content roots
 * (canvas + preview + the block-editor modal) so all content derives its
 * colors / type / radius from the theme. Defaults to the yedalabs blue theme.
 */
export function useLomdaV3Theme(): CSSProperties {
    useBuiltInFontsLoader();
    const theme = useLomdaV3Editor((s) => s.metadata.theme);
    const fontWeights = useLomdaV3Editor((s) => s.metadata.fontWeights);
    // Register @font-face for the lomda's uploaded custom fonts so content that
    // selects one actually renders in it (built-ins are handled above).
    const customFonts = useLomdaV3Editor((s) => s.metadata.style?.customFonts);
    useBrandKitFontFaces(customFonts ?? []);
    // Content-level inner-gap overrides the theme default (settings-panel slider).
    const innerGap = useLomdaV3Editor(
        (s) => s.content.innerGap ?? s.metadata.theme?.innerGap ?? DEFAULT_INNER_GAP
    );
    return useMemo(
        () => ({
            ...buildThemeVars(theme ?? DEFAULT_THEME),
            ...buildFontWeightVars(fontWeights),
            "--lomda-inner-gap": `${innerGap}px`,
            ...CONTENT_ROOT_FONT,
        }),
        [theme, fontWeights, innerGap]
    );
}
